<template>
  <div class="trials-editor">
    <div class="editor-head">
      <h4><i class="bi bi-pencil-square"></i> Trial setup</h4>
      <div class="editor-actions">
        <button class="link-button" @click="$emit('close')">Cancel</button>
        <button class="action-chip confirm" :disabled="saving" @click="save">
          {{ saving ? "Saving…" : "Save changes" }}
        </button>
      </div>
    </div>
    <p class="muted">Edit trial dates, information and deposits. Changes go live on the public sign-up form.</p>

    <!-- Sessions -->
    <section>
      <h5>Trial sessions</h5>
      <div v-for="(s, i) in cfg.sessions" :key="i" class="session-row">
        <div class="grid">
          <label>Label<input v-model="s.label" placeholder="e.g. Junior Trial 1" /></label>
          <label>Date<input v-model="s.date" type="date" /></label>
          <label>Time<input v-model="s.time" type="time" /></label>
          <label>Venue<input v-model="s.venue" /></label>
        </div>
        <label>Details<input v-model="s.description" placeholder="e.g. Sign in from 5pm" /></label>
        <label>Age groups (which players this trial is for)
          <div class="chips">
            <label v-for="a in cfg.ageGroups" :key="a" class="chip-check">
              <input type="checkbox" :value="a" v-model="s.ageGroups" /> {{ a }}
            </label>
          </div>
        </label>
        <button class="link-button danger" @click="cfg.sessions.splice(i, 1)">Remove session</button>
      </div>
      <button class="link-button" @click="addSession">+ Add session</button>
    </section>

    <!-- Info blocks -->
    <section>
      <h5>Information shown on the form</h5>
      <label>Trial information
        <textarea v-model="cfg.info.trials" rows="5"
          placeholder="What to bring, sign-in, Mini Roos vs Juniors info…"></textarea>
      </label>
      <label>Deposit &amp; fees information
        <textarea v-model="cfg.info.fees" rows="5"
          placeholder="Fees explanation, non-refundable deposit, payment steps…"></textarea>
      </label>
    </section>

    <!-- Age groups -->
    <section>
      <h5>Age groups offered</h5>
      <div class="tag-editor">
        <span v-for="(a, i) in cfg.ageGroups" :key="i" class="tag">
          {{ a }} <button @click="removeAgeGroup(i)">&times;</button>
        </span>
        <input v-model="newAgeGroup" placeholder="Add age group…" @keyup.enter="addAgeGroup" />
        <button class="link-button" @click="addAgeGroup">Add</button>
      </div>
    </section>


    <!-- Acknowledgments -->
    <section>
      <h5>Required acknowledgments (tick-boxes)</h5>
      <div v-for="(ack, i) in cfg.acknowledgments" :key="i" class="ack-row">
        <input v-model="cfg.acknowledgments[i]" />
        <button class="link-button danger" @click="cfg.acknowledgments.splice(i, 1)">&times;</button>
      </div>
      <button class="link-button" @click="cfg.acknowledgments.push('')">+ Add acknowledgment</button>
    </section>

    <!-- Codes of conduct (links shown on the sign-up form for families to read) -->
    <section>
      <h5>Codes of conduct</h5>
      <p class="muted">Links shown on the trial form so families can read them before attending (e.g. the Football SA Codes of Conduct).</p>
      <div v-for="(doc, i) in cfg.codesOfConduct" :key="i" class="coc-row">
        <label>Label<input v-model="doc.label" placeholder="e.g. Parents &amp; Junior Players" /></label>
        <label class="grow">URL<input v-model="doc.url" placeholder="https://…" /></label>
        <button class="link-button danger" @click="cfg.codesOfConduct.splice(i, 1)">Remove</button>
      </div>
      <button class="link-button" @click="cfg.codesOfConduct.push({ label: '', url: '' })">+ Add code of conduct</button>
    </section>

    <!-- Deposit bands (charged when a place is offered) -->
    <section>
      <h5>
        Deposit
        <label class="inline-toggle">
          <input type="checkbox" v-model="cfg.deposit.enabled" /> take a deposit when a place is offered
        </label>
      </h5>
      <template v-if="cfg.deposit.enabled">
        <p class="muted">
          The offer email includes a "pay your deposit" link priced by the player's age
          group. Families pay by card through the club's Stripe account, and the inbox
          shows who has paid. Add a band for each price.
        </p>
        <div v-for="(b, i) in cfg.deposit.bands" :key="i" class="band-row">
          <label>Label<input v-model="b.label" placeholder="e.g. All Juniors" /></label>
          <label>Amount ({{ currencySymbol }})
            <input type="number" min="0" step="0.01" :value="(b.amount_pence / 100) || ''"
              @input="b.amount_pence = Math.round(parseFloat($event.target.value || 0) * 100)" />
          </label>
          <label class="grow">Applies to age groups
            <div class="chips">
              <label v-for="a in cfg.ageGroups" :key="a" class="chip-check">
                <input type="checkbox" :value="a" v-model="b.ageGroups" /> {{ a }}
              </label>
            </div>
          </label>
          <button class="link-button danger" @click="cfg.deposit.bands.splice(i, 1)">Remove</button>
        </div>
        <button class="link-button" @click="addBand">+ Add deposit band</button>

        <label class="inline-toggle gate-toggle">
          <input type="checkbox" v-model="cfg.requireDepositForAccess" />
          Require the deposit before app access
        </label>
        <p class="muted">
          When on, a player offered a place through trials can't log in to the app
          (web or phone) until their deposit shows as paid — by card through the
          offer link, or marked paid here for bank transfers. Existing members and
          players who didn't come through trials are not affected. Turn it on once
          you've finished any offers you want to handle outside the app.
        </p>
      </template>
    </section>

    <!-- Offer email (sent by "Email sign-up link" in the inbox) -->
    <section>
      <h5>Offer email</h5>
      <p class="muted">
        Sent when you email a family their sign-up link. Edit it between batches
        (e.g. one wording for U18s / Reserves, another for Mini Roos and Juniors).
        Placeholders are filled in per player:
        <code>{parent_name}</code> <code>{player_name}</code> <code>{team_name}</code>
        <code>{age_group}</code> <code>{club_name}</code> <code>{link}</code>
        <code>{deposit_amount}</code> <code>{deposit_link}</code>.
        Lines mentioning the deposit are left out automatically when no deposit is due.
        <code>{team_name}</code> is whatever you type in "Team offered" on the
        registration, falling back to their age group.
      </p>
      <label>Subject<input v-model="cfg.offerEmail.subject" /></label>
      <label>Message (plain text — a blank line starts a new paragraph)
        <textarea v-model="cfg.offerEmail.body" rows="22" class="email-body"></textarea>
      </label>
    </section>

    <!-- Decline email (offered when a registration is set to Declined) -->
    <section>
      <h5>Decline email</h5>
      <p class="muted">
        Sent only when you choose to — setting a registration to Declined asks
        whether to email the family. Placeholders:
        <code>{parent_name}</code> <code>{player_name}</code>
        <code>{age_group}</code> <code>{club_name}</code>.
      </p>
      <label>Subject<input v-model="cfg.declineEmail.subject" /></label>
      <label>Message (plain text — a blank line starts a new paragraph)
        <textarea v-model="cfg.declineEmail.body" rows="12" class="email-body"></textarea>
      </label>
    </section>

    <p class="error-text" v-if="error">{{ error }}</p>
  </div>
</template>

<script>
import { Trials } from "@/services/trials-service.js";
import { getCurrencySymbol } from "@/config/tenant";

export default {
  name: "TrialsEditor",
  props: {
    clubId: { type: String, required: true },
    initialConfig: { type: Object, required: true },
  },
  emits: ["close", "saved"],
  data() {
    // Deep clone so edits don't mutate the parent until saved.
    const c = JSON.parse(JSON.stringify(this.initialConfig || {}));
    c.info = c.info || { trials: "", fees: "" };
    c.sessions = c.sessions || [];
    c.ageGroups = c.ageGroups || [];
    c.fields = c.fields || {};
    c.deposit = c.deposit || { enabled: false, bands: [] };
    c.deposit.bands = c.deposit.bands || [];
    c.acknowledgments = c.acknowledgments || [];
    c.codesOfConduct = c.codesOfConduct || [];
    c.offerEmail = c.offerEmail || { subject: "", body: "" };
    c.declineEmail = c.declineEmail || { subject: "", body: "" };
    c.requireDepositForAccess = !!c.requireDepositForAccess;
    return { cfg: c, newAgeGroup: "", saving: false, error: "" };
  },
  computed: {
    currencySymbol() {
      return getCurrencySymbol();
    },
  },
  methods: {
    addBand() {
      this.cfg.deposit.bands.push({ label: "", amount_pence: 0, ageGroups: [] });
    },
    addSession() {
      this.cfg.sessions.push({ label: "", date: "", time: "", venue: "", description: "", ageGroups: [] });
    },
    addAgeGroup() {
      const v = this.newAgeGroup.trim();
      if (v && !this.cfg.ageGroups.includes(v)) this.cfg.ageGroups.push(v);
      this.newAgeGroup = "";
    },
    removeAgeGroup(i) {
      this.cfg.ageGroups.splice(i, 1);
    },
    async save() {
      this.saving = true;
      this.error = "";
      try {
        await Trials.saveConfig(this.clubId, this.cfg);
        this.$emit("saved", this.cfg);
      } catch (e) {
        this.error = e?.response?.data?.error || "Could not save.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.trials-editor {
  border: 1px solid #e3e9f0;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fbfcfe;
}

.editor-head {
  display: flex;
  align-items: center;
  justify-content: space-between;

  h4 { margin: 0; font-size: 1rem; }
}

.editor-actions { display: flex; gap: 12px; align-items: center; }

.muted { color: #6c757d; font-size: 0.83rem; }

section {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eef1f4;

  h5 {
    font-size: 0.9rem;
    margin: 0 0 8px;
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

label {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 8px;

  input, textarea {
    padding: 7px 9px;
    border: 1px solid #ced4da;
    border-radius: 6px;
    font-weight: 400;
    font-size: 0.88rem;
  }

  textarea.email-body {
    font-family: inherit;
    line-height: 1.45;
  }
}

.muted code {
  background: #f1f3f5;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 0.78rem;
}

.inline-toggle {
  flex-direction: row;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  font-size: 0.85rem;

  input { width: 16px; height: 16px; }
}

.session-row, .band-row {
  border: 1px solid #e9edf1;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 10px;
  background: #fff;
}

.gate-toggle {
  margin-top: 12px;
  font-weight: 600;
}

.band-row {
  display: grid;
  grid-template-columns: 1fr 1fr 2fr auto;
  gap: 10px;
  align-items: start;
}

.grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 10px;

  @media (max-width: 640px) { grid-template-columns: 1fr 1fr; }
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 3px;
}

.chip-check {
  flex-direction: row;
  align-items: center;
  gap: 4px;
  font-weight: 400;
  font-size: 0.8rem;
  background: #f1f3f5;
  border-radius: 6px;
  padding: 3px 8px;
  margin: 0;

  input { width: 14px; height: 14px; }
}

.tag-editor {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;

  input {
    padding: 6px 8px;
    border: 1px solid #ced4da;
    border-radius: 6px;
  }
}

.tag {
  background: #e9ecef;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 0.82rem;

  button {
    border: none;
    background: none;
    color: #6c757d;
    cursor: pointer;
    padding: 0 0 0 4px;
  }
}

.ack-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;

  input {
    flex: 1;
    padding: 7px 9px;
    border: 1px solid #ced4da;
    border-radius: 6px;
  }
}

.grow { grid-column: auto; }

.coc-row {
  display: grid;
  grid-template-columns: 1fr 2fr auto;
  gap: 10px;
  align-items: end;
  margin-bottom: 8px;
}

.action-chip {
  border: 1px solid #ced4da;
  background: #fff;
  color: #212529;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 0.85rem;
  cursor: pointer;

  &.confirm { background: #198754; color: #fff; border-color: #198754; }
  &:disabled { opacity: 0.6; }
}

.link-button {
  border: none;
  background: none;
  color: #0d6efd;
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.83rem;
  padding: 0;

  &.danger { color: #dc3545; }
}

.error-text { color: #dc3545; font-size: 0.85rem; }
</style>
